import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translatePropertyType, translateStatus } from '../utils/translations';
import { useTranslations } from '../utils/translationDictionaries';
import './PropertyCard.css';

const STATUS_FLAIR = ['sold', 'pending', 'rented'];

const PropertyCard = ({ property }) => {
  const { language } = useLanguage();
  const { t } = useTranslations(language);
  const formatPrice = (price, listingType) => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);

    return listingType === 'for-rent' ? `${formatted}/mes` : formatted;
  };

  // Default listingType to 'for-sale' if not set
  const listingType = property.listingType || 'for-sale';

  const showStatusFlair = STATUS_FLAIR.includes(property.status);
  const hasValue = (value) => value !== undefined && value !== null && value !== '';
  const propertySize = hasValue(property.area) && Number(property.area) > 0
    ? `${property.area} ft²`
    : hasValue(property.lotSizeM2) && Number(property.lotSizeM2) > 0
      ? `${property.lotSizeM2} m²`
      : null;
  const propertyAddress = [
    property.address,
    property.city,
    property.state && `${property.state}${property.zipCode ? ` ${property.zipCode}` : ''}`,
  ].filter(Boolean).join(', ');

  return (
    <Link to={`/property/${property.id}`} className="property-card">
      <div className="property-card-image">
        <img
          src={property.mainImage || '/placeholder.jpg'}
          alt={property.title}
          onError={(e) => {
            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ddd" width="400" height="400"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
          }}
        />
        {property.isReposeida && (
          <div className="property-reposeida-badge">Reposeida</div>
        )}
        {showStatusFlair && (
          <div className="property-status-flair">
            {translateStatus(property.status, language)}
          </div>
        )}
      </div>
      <div className="property-card-content">
        {propertyAddress && (
          <p className="property-card-address" title={propertyAddress}>
            {propertyAddress}
          </p>
        )}
        <p className="property-price">{formatPrice(property.price, listingType)}</p>
        {(hasValue(property.bedrooms) || hasValue(property.bathrooms) || propertySize || property.propertyType) && (
          <div className="property-card-details">
            {hasValue(property.bedrooms) && (
              <span className="property-card-detail">
                <strong>{property.bedrooms}</strong> {t('propertyDetails.bedrooms')}
              </span>
            )}
            {hasValue(property.bathrooms) && (
              <span className="property-card-detail">
                <strong>{property.bathrooms}</strong> {t('propertyDetails.bathrooms')}
              </span>
            )}
            {propertySize && (
              <span className="property-card-detail">
                <strong>{propertySize}</strong> {t('propertyDetails.area')}
              </span>
            )}
            {property.propertyType && (
              <span className="property-card-detail property-card-type">
                {translatePropertyType(property.propertyType, language)}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

export default PropertyCard;
